import { Company } from '@app/core/model/company.model'
import { FeeAmountType, FeeItemInstance } from '@app/features/scope-overview/model/fee-item.model'
import { LibraryComponent } from '../component/library-component.model'
import { LibraryEntry } from '../library-entry.model'
import { LibraryRateCard } from '../library-rate-card.model'
import { LibrarySection } from '../library-section.model'
import { Money } from '@app/features/scope-overview/model/money.model'
import { ThirdPartyCost } from '@app/core/model/third-party-cost.model'
import { Type } from 'class-transformer'
import { User } from '@app/core/model/user.model'
import { isNil } from 'lodash'

export class LibraryDeliverableEntry extends LibraryEntry {
  id: number
  name: string
  language: string
  rateCard: LibraryRateCard
  fixedCostEditable: boolean
  disciplineTypeId: number
  disciplineTypeName: string
  @Type(() => LibrarySection) sections: LibrarySection[]
  @Type(() => LibraryComponent) libraryComponents: LibraryComponent[]
  @Type(() => ThirdPartyCost) thirdPartyCosts: ThirdPartyCost[]
  feeItems: FeeItemInstance[]
  deliverable: { id: number; name: string }
  description: string
  extraDescription: string
  internalNote: string
  createdTs: string
  updatedTs: string
  @Type(() => User) createdBy: User
  @Type(() => User) updatedBy: User
  archived: boolean
  fixedPricing: boolean
  @Type(() => Money) cachedTotalValue: Money
  @Type(() => Money) fixedCostOffset: Money
  privilegeCategories: string[]
  initialized: boolean
  deliverableDynamicFieldSettingMappings: DeliverableDynamicFieldSettingMapping[]

  @Type(() => Company) company: Company
  compoundKey: { ratecardVersionId: 1094; disciplineId: 1 }

  get value(): Money {
    return this.getValue()
  }

  getValue(ctx?: { excludeFees?: boolean }): Money {
    let value: Money

    if (!this.libraryComponents?.length)
      value = this.cachedTotalValue ? this.cachedTotalValue : Money.of(0, this.rateCardVersion?.currencyCode)

    this.libraryComponents.forEach((component) => {
      const price = component.getRateCardPrice()
      value = value ? value.add(price) : price
      value = this.accumulateValue(value, component.thirdPartyCosts)
    })

    if (this.thirdPartyCosts) value = this.accumulateValue(value, this.thirdPartyCosts)

    this.sections.forEach((section) => {
      if (section.thirdPartyCosts) value = this.accumulateValue(value, section.thirdPartyCosts)
    })

    if (value && this.fixedCostOffset != null) value = Money.addValues(value, this.fixedCostOffset)

    if (ctx && !ctx.excludeFees && value && !this.fixedCostOffset)
      value = value.addValue(this.getTotalFees(value.amount))

    return value
  }

  getTotalFees(amount): number {
    let total = 0

    if (this.feeItems?.length)
      this.feeItems.forEach((fee) => {
        total += this.getFeePrice(fee, amount)
      })

    return +total.toFixed(2)
  }

  getFeePrice(fee: FeeItemInstance, amount: number): number {
    if (fee.feeItem.amountType === FeeAmountType.MONETARY) return parseFloat(fee.feeItem.amount.toFixed(2))
    else
      return isNil(amount)
        ? parseFloat(((this.getValue({ excludeFees: true }).amount * fee.feeItem.amount) / 100).toFixed(2))
        : parseFloat(((Number(amount) * Number(fee.feeItem.amount)) / 100).toFixed(2))
  }

  private accumulateValue(value: Money, thirdPartyCosts: ThirdPartyCost[]): Money {
    thirdPartyCosts.forEach((tpc) => {
      const price = tpc.calculateSellingPrice()
      if (price) value = value ? value.add(price) : price
    })
    return value
  }
}

export class DeliverableDynamicFieldSettingMapping {
  id: number
  defaultValue: string
  deliverableDynamicFieldSetting: any
}
